import type { CSSProperties } from 'react';

const F = "'Figtree', sans-serif";
const HERO_BG = '/andbook/mumbai/mumbai.jpg';
const CONTENT_W = 'min(1040px, calc(100% - 48px))';

function Bar({ w, h = 12, light = false, style }: { w: number | string; h?: number; light?: boolean; style?: CSSProperties }) {
  return (
    <div
      style={{
        width: w,
        height: h,
        borderRadius: h / 2,
        background: light ? 'rgba(255,255,255,0.24)' : 'linear-gradient(90deg,#eef1f6 25%,#e2e7f0 37%,#eef1f6 63%)',
        backgroundSize: '400% 100%',
        animation: light ? 'ab-sk-pulse 1.4s ease-in-out infinite' : 'ab-sk-shimmer 1.4s linear infinite',
        ...style,
      }}
    />
  );
}

/** Neutral, page-agnostic loader for AndBook subpages (does NOT look like the home page). */
export function AndBookRouteFallback(): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 14 }}>
      <style>{`@keyframes ab-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{
        width: 34, height: 34, borderRadius: '50%',
        border: '3px solid #E8EDF5', borderTopColor: '#D4930D',
        animation: 'ab-spin 0.8s linear infinite',
      }} />
      <div style={{ font: `600 13px ${F}`, color: '#8895A7', letterSpacing: 0.2 }}>Loading…</div>
    </div>
  );
}

/** Loading placeholder mirroring the AndBook front page (inset hero + centered search card). */
export default function AndBookSkeleton(): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 60, background: '#fff' }}>
      <style>{`
        @keyframes ab-sk-shimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}
        @keyframes ab-sk-pulse{0%,100%{opacity:.5}50%{opacity:.9}}
      `}</style>

      {/* Inset hero */}
      <div style={{ padding: '22px 24px 0' }}>
        <div style={{
          position: 'relative', overflow: 'hidden', borderRadius: 22, minHeight: 288,
          backgroundImage: `linear-gradient(103deg, rgba(9,16,34,0.94) 0%, rgba(15,27,52,0.78) 40%, rgba(20,34,62,0.4) 100%), url('${HERO_BG}')`,
          backgroundSize: 'cover', backgroundPosition: 'center 45%',
          padding: '46px 48px 120px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Bar w={96} h={22} light style={{ borderRadius: 99 }} />
            <Bar w={300} h={30} light />
            <Bar w={360} h={14} light />
          </div>
          <Bar w={132} h={40} light style={{ borderRadius: 10 }} />
        </div>
      </div>

      {/* Floating search card */}
      <div style={{ width: CONTENT_W, margin: '-84px auto 0', position: 'relative', zIndex: 2 }}>
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 8px 32px rgba(27,45,79,0.12)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 36, padding: '18px 0', borderBottom: '1px solid #E8ECF1' }}>
            <Bar w={110} />
            <Bar w={120} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ padding: '22px 24px', borderRight: i < 3 ? '1px solid #E8ECF1' : 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Bar w={70} h={9} />
                <Bar w="80%" h={20} />
              </div>
            ))}
          </div>
          <div style={{ padding: '14px 24px', background: '#FAFBFC', borderTop: '1px solid #E8ECF1', display: 'flex', justifyContent: 'center' }}>
            <Bar w={160} h={42} style={{ borderRadius: 8 }} />
          </div>
        </div>
      </div>

      {/* Value strip */}
      <div style={{ width: CONTENT_W, margin: '18px auto 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, background: '#fff', border: '1px solid #E8EDF5', borderRadius: 14, padding: '12px 14px' }}>
            <Bar w={38} h={38} style={{ borderRadius: 10 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Bar w="55%" h={12} />
              <Bar w="80%" h={10} />
            </div>
          </div>
        ))}
      </div>

      {/* Explore cards */}
      <div style={{ width: CONTENT_W, margin: '34px auto 0' }}>
        <Bar w={170} h={18} style={{ marginBottom: 14 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))', gap: 16 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid #E8EDF5' }}>
              <Bar w="100%" h={142} style={{ borderRadius: 0 }} />
              <div style={{ padding: '12px 14px', display: 'flex', justifyContent: 'space-between' }}>
                <Bar w="60%" h={12} />
                <Bar w={16} h={12} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
